import React, { useState, useEffect } from 'react';
import { AlertTriangle, Package, CheckCircle2, Clock, ChevronRight, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Line } from 'recharts';
import axios from 'axios';
import KPICard from './KPICard';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════ DELIVERY PERFORMANCE REPORT ═══════════════════════
function DeliveryReport({ category }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProject, setExpandedProject] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = category ? `?category=${encodeURIComponent(category)}` : '';
        const res = await axios.get(`${API}/api/planning/report/delivery-performance${params}`);
        setData(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [category]);

  if (loading) return <div className="text-center py-10 text-slate-400">Loading...</div>;
  if (!data?.projects?.length) return <div className="text-center py-10 text-slate-400">No delivery data available. Make sure projects have planned sets with dock dates.</div>;

  const { projects, ship_offset, today, very_late_days: vld } = data;
  const veryLateDays = vld || 7;

  const totals = projects.reduce((acc, p) => ({
    on_time: acc.on_time + p.on_time, late: acc.late + p.late, very_late: acc.very_late + p.very_late,
    not_shipped: acc.not_shipped + p.not_shipped, not_planned: acc.not_planned + p.not_planned,
    ordered: acc.ordered + p.ordered, planned: acc.planned + p.planned,
  }), { on_time: 0, late: 0, very_late: 0, not_shipped: 0, not_planned: 0, ordered: 0, planned: 0 });

  const chartData = projects.filter(p => p.planned > 0 || p.not_planned > 0).map(p => ({
    name: p.project_name.length > 12 ? p.project_name.slice(0, 12) + '..' : p.project_name,
    fullName: p.project_name,
    'On Time': p.on_time, [`Late (1-${veryLateDays}d)`]: p.late, [`Very Late (>${veryLateDays}d)`]: p.very_late,
    'Not Shipped': p.not_shipped, 'Not Planned': p.not_planned,
  }));

  const pieData = [
    { name: 'On Time', value: totals.on_time, color: '#10b981' },
    { name: 'Late', value: totals.late, color: '#f59e0b' },
    { name: 'Very Late', value: totals.very_late, color: '#ef4444' },
    { name: 'Not Shipped', value: totals.not_shipped, color: '#6366f1' },
    { name: 'Not Planned', value: totals.not_planned, color: '#94a3b8' },
  ].filter(d => d.value > 0);

  const statusColor = (s) => ({ on_time: 'bg-emerald-100 text-emerald-700', late: 'bg-amber-100 text-amber-700', very_late: 'bg-red-100 text-red-700', not_shipped: 'bg-indigo-100 text-indigo-700' }[s] || 'bg-slate-100 text-slate-500');
  const statusLabel = (s) => ({ on_time: 'On Time', late: 'Late', very_late: 'Very Late', not_shipped: 'Not Shipped' }[s] || s);

  return (
    <div className="space-y-4" data-testid="delivery-report">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <KPICard label="On Time" value={totals.on_time} icon={<CheckCircle2 className="w-5 h-5" />} color="emerald" badge="sets" />
        <KPICard label={`Late (1-${veryLateDays}d)`} value={totals.late} icon={<Clock className="w-5 h-5" />} color="amber" badge="sets" />
        <KPICard label={`Very Late (>${veryLateDays}d)`} value={totals.very_late} icon={<AlertTriangle className="w-5 h-5" />} color="red" badge="sets" />
        <KPICard label="Not Shipped" value={totals.not_shipped} icon={<Package className="w-5 h-5" />} color="blue" badge="sets" />
        <KPICard label="Not Planned" value={totals.not_planned} icon={<Calendar className="w-5 h-5" />} badge="sets"
          subtitle={`${totals.ordered} ordered / ${totals.planned} planned`} />
      </div>
      <div className="text-[10px] text-slate-400 text-right">Ship offset: Plan Date - {ship_offset} days | Very Late &gt; {veryLateDays}d | As of {today}</div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-xs font-bold text-slate-700 mb-3">Delivery Performance by Project</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} labelFormatter={(v, payload) => payload?.[0]?.payload?.fullName || v} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="On Time" stackId="a" fill="#10b981" />
              <Bar dataKey={`Late (1-${veryLateDays}d)`} stackId="a" fill="#f59e0b" />
              <Bar dataKey={`Very Late (>${veryLateDays}d)`} stackId="a" fill="#ef4444" />
              <Bar dataKey="Not Shipped" stackId="a" fill="#6366f1" />
              <Bar dataKey="Not Planned" stackId="a" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-xs font-bold text-slate-700 mb-3">Overall Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={80} dataKey="value" paddingAngle={2}>
                {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 justify-center mt-2">
            {pieData.map(d => (
              <span key={d.name} className="flex items-center gap-1 text-[9px] font-semibold text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                {d.name}: {d.value}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
          <h3 className="text-xs font-bold text-slate-700">Project Breakdown</h3>
        </div>
        <table className="w-full text-[11px]">
          <thead>
            <tr className="bg-slate-50/50 text-[9px] uppercase text-slate-400 font-bold">
              <th className="px-3 py-1.5 text-left w-5"></th>
              <th className="px-3 py-1.5 text-left">Project</th>
              <th className="px-3 py-1.5 text-left">PROGRAM #</th>
              <th className="px-3 py-1.5 text-center">Ordered</th>
              <th className="px-3 py-1.5 text-center text-emerald-600">On Time</th>
              <th className="px-3 py-1.5 text-center text-amber-600">Late</th>
              <th className="px-3 py-1.5 text-center text-red-600">Very Late</th>
              <th className="px-3 py-1.5 text-center text-indigo-600">Not Shipped</th>
              <th className="px-3 py-1.5 text-center">Not Planned</th>
              <th className="px-3 py-1.5 text-center">Avg Delay</th>
            </tr>
          </thead>
          <tbody>
            {projects.map(p => (
              <React.Fragment key={p.sr_number}>
                <tr className={`border-t border-slate-100 hover:bg-slate-50/50 cursor-pointer ${expandedProject === p.sr_number ? 'bg-violet-50/30' : ''}`}
                  onClick={() => setExpandedProject(expandedProject === p.sr_number ? null : p.sr_number)}>
                  <td className="px-3 py-2 text-slate-400">
                    <ChevronRight className={`w-3 h-3 transition-transform ${expandedProject === p.sr_number ? 'rotate-90 text-violet-600' : ''}`} />
                  </td>
                  <td className="px-3 py-2 font-semibold text-slate-700">{p.project_name}</td>
                  <td className="px-3 py-2 text-slate-500">{p.sr_number}</td>
                  <td className="px-3 py-2 text-center font-bold text-slate-600">{p.ordered}</td>
                  <td className="px-3 py-2 text-center font-bold text-emerald-600">{p.on_time || '-'}</td>
                  <td className="px-3 py-2 text-center font-bold text-amber-600">{p.late || '-'}</td>
                  <td className="px-3 py-2 text-center font-bold text-red-600">{p.very_late || '-'}</td>
                  <td className="px-3 py-2 text-center font-bold text-indigo-600">{p.not_shipped || '-'}</td>
                  <td className="px-3 py-2 text-center font-bold text-slate-400">{p.not_planned || '-'}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${p.avg_delay_days > veryLateDays ? 'bg-red-100 text-red-700' : p.avg_delay_days > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {p.avg_delay_days}d
                    </span>
                  </td>
                </tr>
                {expandedProject === p.sr_number && p.sets_detail.length > 0 && (
                  <tr>
                    <td colSpan={10} className="p-0">
                      <div className="bg-slate-50/70 border-y border-slate-200 max-h-[400px] overflow-y-auto">
                        <table className="w-full text-[10px]">
                          <thead className="sticky top-0 bg-slate-100">
                            <tr className="text-[8px] uppercase text-slate-400 font-bold">
                              <th className="px-4 py-1 text-left">Set #</th>
                              <th className="px-3 py-1 text-left">Line</th>
                              <th className="px-3 py-1 text-center">Plan Date</th>
                              <th className="px-3 py-1 text-center">Expected Ship</th>
                              <th className="px-3 py-1 text-center">Actual Ship</th>
                              <th className="px-3 py-1 text-center">Delay</th>
                              <th className="px-3 py-1 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.sets_detail.map((s, idx) => (
                              <tr key={`${s.set_number}-${s.plan_date}-${idx}`} className="border-t border-slate-100/80 hover:bg-white/60">
                                <td className="px-4 py-1.5 font-bold text-slate-700">Set {s.set_number}</td>
                                <td className="px-3 py-1.5 text-slate-500">{s.line_name || '-'}</td>
                                <td className="px-3 py-1.5 text-center text-slate-600">{s.plan_date}</td>
                                <td className="px-3 py-1.5 text-center text-blue-600 font-semibold">{s.expected_ship}</td>
                                <td className="px-3 py-1.5 text-center font-semibold">{s.actual_ship || <span className="text-slate-300">-</span>}</td>
                                <td className="px-3 py-1.5 text-center">
                                  <span className={`font-bold ${s.delay_days > veryLateDays ? 'text-red-600' : s.delay_days > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                    {s.delay_days > 0 ? `+${s.delay_days}d` : s.delay_days === 0 ? '0d' : `${s.delay_days}d`}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-center">
                                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${statusColor(s.status)}`}>
                                    {statusLabel(s.status)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DeliveryReport;
